import * as Sentry from '@sentry/node';
import {nodeProfilingIntegration} from '@sentry/profiling-node';
import {Environment} from 'common/tool/Environment';
import {ExceptionContract} from 'common/exception/ExceptionContract';
import {UnauthorizedException} from 'common/exception/UnauthorizedException';
import {Env} from 'common/tool/Env';
import * as express from 'express';
import {isNullOrUndefined} from 'common/util/HelperUtil';

export class Logger {
    public static init(sentryToken: string, app: express.Express): void {
        if (Environment.getMode() === Env.PRODUCTION) {
            // const tracesSampleRate: number = 0.001;
            const tracesSampleRate: number = 0; // TODO enable tracing
            Sentry.init({
                dsn: sentryToken,
                integrations: [
                    Sentry.captureConsoleIntegration({
                        levels: ['error', 'warn', 'log']
                    }),
                    nodeProfilingIntegration()
                ],
                environment: Environment.getMode(),
                maxBreadcrumbs: 50,
                tracesSampleRate: tracesSampleRate,
                profilesSampleRate: tracesSampleRate
            });
            Sentry.setupExpressErrorHandler(app);
        }
    }

    public static trace(message: string, extra?: any): void {
        if ([Env.DEVELOPMENT, Env.TEST].includes(Environment.getMode())) {
            if (extra === undefined) {
                console.info(message); // eslint-disable-line no-console
            } else {
                console.info(message, JSON.stringify(extra, null, 2)); // eslint-disable-line no-console
            }
        } else {
            Sentry.addBreadcrumb({
                category: 'info',
                message: message,
                data: extra,
                level: 'info'
            });
        }
    }

    public static debug(message: string, extra?: any): void {
        if ([Env.DEVELOPMENT, Env.TEST].includes(Environment.getMode())) {
            if (extra === undefined) {
                console.log(message); // eslint-disable-line no-console
            } else {
                console.log(message, JSON.stringify(extra, null, 2)); // eslint-disable-line no-console
            }
        } else {
            Sentry.addBreadcrumb({
                category: 'debug',
                message: message,
                data: extra,
                level: 'debug'
            });
        }
    }

    public static info(message: string, extra?: any): void {
        if ([Env.DEVELOPMENT, Env.TEST].includes(Environment.getMode())) {
            if (extra === undefined) {
                console.info(message); // eslint-disable-line no-console
            } else {
                console.info(message, JSON.stringify(extra, null, 2)); // eslint-disable-line no-console
            }
        } else {
            Sentry.withScope(scope => {
                if (extra !== undefined) {
                    scope.setExtra('extra', JSON.stringify(extra));
                }
                Sentry.captureMessage(message);
            });
        }
    }

    public static warning(error: ExceptionContract, extra?: any): void {
        if (!isNullOrUndefined((error as any).response)) {
            if (typeof extra !== 'object') {
                extra = {};
            }
            extra.requestErrorData = (error as any).response.data;
        }
        if ([Env.DEVELOPMENT, Env.TEST].includes(Environment.getMode())) {
            if (extra === undefined) {
                console.warn(error); // eslint-disable-line no-console
            } else {
                console.warn(error, JSON.stringify(extra, null, 2)); // eslint-disable-line no-console
            }
        } else {
            Sentry.withScope(scope => {
                if (extra !== undefined) {
                    scope.setExtra('extra', JSON.stringify(extra));
                }
                if (error.data !== undefined) {
                    scope.setExtra('data', JSON.stringify(error.data));
                }
                Sentry.captureException(error);
            });
        }
    }

    public static error(error: ExceptionContract, extra?: any): void {
        if ([UnauthorizedException.ERROR_CODE].includes(error.code)) {
            return;
        }
        if (!isNullOrUndefined((error as any).response)) {
            if (typeof extra !== 'object') {
                extra = {};
            }
            extra.requestErrorData = (error as any).response.data;
        }
        if ([Env.DEVELOPMENT, Env.TEST].includes(Environment.getMode())) {
            if (extra === undefined) {
                console.error(error); // eslint-disable-line no-console
            } else {
                console.error(error, JSON.stringify(extra, null, 2)); // eslint-disable-line no-console
            }
        } else {
            Sentry.withScope(scope => {
                if (extra !== undefined) {
                    scope.setExtra('extra', JSON.stringify(extra));
                }
                if (error.data !== undefined) {
                    scope.setExtra('data', JSON.stringify(error.data));
                }
                Sentry.captureException(error);
            });
        }
    }
}
